import { Component, Host, h, Listen, Watch, Element, Prop, Method } from '@stencil/core';
import { Debounce } from '../../utils/decorators/debounce/debounce.decorator';
import { DescendantsObserver } from '../../utils/descendants-observer';

/**
 * @slot - Tree content.
 */
@Component({
  tag: 'sy-tree',
  styleUrls: ['scss/index.scss', 'scss/_tokens.scss'],
  shadow: true,
})
export class Tree {
  private descendantsObserver: DescendantsObserver;

  @Element() el: HTMLSyTreeElement;

  /**
   * If true, when a leaf is activated all parents are activated too.
   */
  @Prop({ reflect: true }) activePath = false;

  /**
   * Sets arrow icon to display on the start or end of the tree node.
   */
  @Prop({ reflect: true }) alignArrow: 'start' | 'end' = 'start';

  /**
   * Disables all animations from all items
   */
  @Prop() disableAnimations = false;
  @Watch('disableAnimations')
  disableAnimationsWatcher(): void {
    this.setChildrenDisableAnimations();
  }

  componentWillLoad() {
    this.descendantsObserver = new DescendantsObserver('sy-tree-node', (array: Array<any>) => {
      this.setDisableAnimation(array);
    });

    this.descendantsObserver.observe(this.el, {
      childList: true,
      subtree: true,
    });
  }

  componentDidLoad() {
    this.setChildrenDisableAnimations();
  }

  disconnectedCallback() {
    if (this.el.isConnected) return;

    this.descendantsObserver?.disconnect();
  }

  @Listen('syTreeNodeDidActivate')
  onSyTreeNodeDidActivate(e: any): void {
    const activeNode: HTMLElement = e.target;
    this.el.querySelectorAll('sy-tree-node').forEach((node: HTMLSyTreeNodeElement) => {
      if ((this.activePath && node.contains(activeNode)) || node === activeNode) {
        node.activate(false);
      } else {
        node.deactivate(false);
      }
    });
  }

  @Listen('syTreeNodeDidExpand')
  onSyTreeNodeDidExpand(e: any): void {
    const expandedNode: HTMLElement = e.target;
    this.el.querySelectorAll('sy-tree-node[type=inner-node]').forEach((node: HTMLSyTreeNodeElement) => {
      if (!node.contains(expandedNode) && node === expandedNode) {
        node.collapse(false, this.disableAnimations);
      }
    });
  }

  /**
   * Returns all the active tree-nodes
   */
  @Method()
  async getActiveNodes(): Promise<NodeListOf<HTMLSyTreeNodeElement>> {
    return this.el.querySelectorAll('sy-tree-node[active]');
  }

  /**
   * Returns the active leaf
   */
  @Method()
  async getActiveLeaf(): Promise<HTMLSyTreeNodeElement> {
    return this.el.querySelector('sy-tree-node[active][type=leaf]') as HTMLSyTreeNodeElement;
  }

  /**
   * Set a node as active
   * @param node selector | HTMLSyTreeNodeElement
   */
  @Method()
  async setActiveNode(node: string | HTMLSyTreeNodeElement): Promise<void> {
    let element: HTMLSyTreeNodeElement;
    if (typeof node === 'string') {
      element = this.el.querySelector(node) as HTMLSyTreeNodeElement;
    } else {
      element = node;
    }

    if (element?.nodeName.toLocaleLowerCase() === 'sy-tree-node') {
      return element.activate();
    }
  }

  /**
   * Expand all tree-nodes
   * If instant is true, there won't have open animation in all items.
   */
  @Method()
  async expandAll(instant = false): Promise<void> {
    const promises: Promise<void>[] = [];
    this.el
      .querySelectorAll('sy-tree-node[type=inner-node]')
      .forEach((node: HTMLSyTreeNodeElement) => promises.push(node.expand(false, instant)));
    await Promise.all(promises);
  }

  /**
   * Collapse all tree-nodes
   * If instant is true, there won't have close animation in all items.
   */
  @Method()
  async collapseAll(instant = false): Promise<void> {
    const promises: Promise<void>[] = [];
    this.el
      .querySelectorAll('sy-tree-node[type=inner-node]')
      .forEach((node: HTMLSyTreeNodeElement) => promises.push(node.collapse(false, instant)));
    await Promise.all(promises);
  }

  @Debounce(300)
  private setDisableAnimation(addedTreeNode) {
    addedTreeNode.forEach((treeNode) => {
      if (treeNode.isConnected) {
        treeNode.disableAnimations = this.disableAnimations;
      }
    });
  }

  private setChildrenDisableAnimations() {
    this.el.querySelectorAll('sy-tree-node').forEach((item) => {
      item.disableAnimations = this.disableAnimations;
    });
  }

  render() {
    return (
      <Host>
        <slot></slot>
      </Host>
    );
  }
}
